import type { AppRouter } from '@cloud/backend'
import { createORPCClient } from '@orpc/client'
import { RPCLink } from '@orpc/client/fetch'
import type { RouterClient } from '@orpc/server'
import { createTanstackQueryUtils } from '@orpc/tanstack-query'
import { fireAuthFailure, isPublicPath } from './auth-failure'
import { fireWorkspaceReadOnly } from './workspace-readonly'

/**
 * oRPC client — configured to call the backend's /api/v1/* endpoints.
 *
 * The backend runs on a separate port (default 8787) and handles all
 * API logic. The client sends cookies for session-based auth
 * (better-auth + tenant `oid`/`uid` cookies).
 *
 * Any 401 response triggers the global auth-failure bus, which the
 * UserProvider subscribes to so it can do a full logout + redirect.
 * This is how the dashboard recovers from a stale session: instead
 * of rendering broken data with `user=null`, we force the user back
 * to the login screen so they can sign in fresh.
 */

export const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8787'

const link = new RPCLink({
  url: `${BACKEND_URL}/api/v1`,
  fetch: async (input, init) => {
    const res = await fetch(input, {
      ...init,
      credentials: 'include', // Send cookies for auth
    })
    if (typeof window !== 'undefined' && !isPublicPath(window.location.pathname)) {
      // On UNAUTHORIZED, fire the global auth-failure event. The
      // handler verifies the session is actually dead (via whoami)
      // before doing a full logout — this prevents transient 401s
      // during backend restarts from force-logging the user out.
      if (res.status === 401) {
        fireAuthFailure(BACKEND_URL)
      }
      // 2FA enforcement: the backend returns 403 with error code
      // TWO_FACTOR_REQUIRED when an admin hasn't enrolled in 2FA.
      // Redirect them to the enrollment page instead of dead-ending.
      if (res.status === 403) {
        try {
          const body = await res.clone().json()
          if (body?.error === 'TWO_FACTOR_REQUIRED') {
            window.location.replace('/setup-2fa')
          }
        } catch {
          // Not JSON or unreadable — fall through
        }
      }
      // 423 LOCKED: the workspace is in a non-active lifecycle state
      // (paused or pending_deletion). The auth middleware blocks
      // writes against the tenant DB and returns this status. We
      // fire the workspace-readonly event so the UserProvider can
      // refresh whoami; the banner then appears within seconds even
      // if the user hasn't navigated. The 423 itself still bubbles
      // up to the caller as a normal oRPC error so individual
      // mutations can show their own failure toast.
      if (res.status === 423) {
        fireWorkspaceReadOnly()
      }
    }
    return res
  },
})

// Create the base client typed with the backend router type
// This properly types all the procedure calls
export const client = createORPCClient(link) as RouterClient<AppRouter>

// Create the query utils with proper typing from the router
export const orpc = createTanstackQueryUtils(client)
